import React, { useEffect, useState, useRef } from "react";
import { BrandTelegram, Envelope, Eye, Grid, List, Telephone, Trash, Users } from "@mynaui/icons-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { FileSearch, Trophy, UserCheck, CalendarDays, Ban, ShieldCheck } from "lucide-react";
import { ITournamentItem } from "../../models/ITournament";
import moment from "moment";
import {
  Avatar,
  EmptyState,
  IconButton,
  Menu,
  Pagination,
  SearchInput,
  Segmented,
  SkeletonRows,
  SortHeader,
  TableWrap,
  Tag,
  Toolbar,
} from "../../ui";
import type { MenuItem, TagTone } from "../../ui";
import "./styles.scss";

type Column<T> = {
  key: keyof T;
  label: string;
  uppercase?: boolean;
  center?: boolean;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  sortable?: boolean;
  stickyRight?: boolean;
};

type DataTableProps<T> = {
  id: string;
  data: T[];
  label: string;
  includeGrid?: boolean;
  columns: Column<T>[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  editable?: boolean;
  canDelete?: boolean;
  canBan?: boolean;
  loading?: boolean;
  onDelete?: (item: T) => void;
  onBan?: (item: T) => void;
  onSearch?: (value: string) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onViewItem: (item: T) => void;
  onSwitchRole?: (item: T) => void;
  onPageChange: (page: number) => void;
  onSortChange: (columnKey: any, direction: "asc" | "desc") => void;
  sortColumn: any;
  sortDirection: "asc" | "desc";
};

// The grid cards' status tags (tournaments)
const STATUS_TONES: Record<string, TagTone> = {
  open: "info",
  active: "ok",
  closed: "danger",
  "not-open": "warn",
  completed: "neutral",
};

// A tournament's sport tag: padel blue, volleyball orange, beach volleyball yellow
const sportTone = (type?: string): TagTone => {
  const t = (type || "").toLowerCase();
  if (t.includes("beach volleyball")) return "yellow";
  if (t.includes("padel")) return "brand";
  if (t.includes("volleyball")) return "spark";
  return "neutral";
};

const DataTable = <T extends object>({
  data,
  columns,
  label,
  pageNumber,
  totalPages,
  totalItems,
  pageSize,
  includeGrid,
  canDelete,
  canBan,
  editable,
  loading,
  onDelete,
  onBan,
  onSearch,
  searchValue,
  onSearchChange,
  onPageChange,
  onSortChange,
  onViewItem,
  onSwitchRole,
  sortColumn,
  sortDirection,
  id,
}: DataTableProps<T>) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [selectedListOption, setSelectedListOption] = useState<"list" | "grid">(
    window.innerWidth < 768 && includeGrid ? "grid" : "list"
  );
  const [search, setSearch] = useState(searchValue || "");

  useEffect(() => {
    const listType = searchParams.get("listType") as "grid" | "list" | null;
    if (listType) setSelectedListOption(listType);
  }, []);

  useEffect(() => {
    if (searchValue !== undefined) {
      setSearch(searchValue);
    }
  }, [searchValue]);

  // Note: Removed auto-focus effect as it was causing performance issues
  // Users can manually focus the search input if needed

  const handleSort = (columnKey: keyof T) => {
    if (sortColumn === columnKey) {
      onSortChange(columnKey, sortDirection === "asc" ? "desc" : "asc");
    } else {
      onSortChange(columnKey, "asc");
    }
  };

  const handleOptionClick = (item: "list" | "grid") => {
    setSelectedListOption(item);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev.toString());
      p.set("listType", item);
      return p;
    });
  };

  const hasSearch = !!(onSearch || (searchValue !== undefined && onSearchChange));
  const columnCount = columns.length + (editable ? 1 : 0);

  // The row's ⋮ menu: the same actions, in the same order, under the same conditions as before
  const rowMenuItems = (row: T): MenuItem[] => {
    const r = row as any;
    return [
      { label: "View", icon: <Eye size={16} />, onClick: () => onViewItem(row) },
      {
        label: "Whatsapp",
        icon: <Telephone size={16} />,
        visible: !!r.phoneNumber,
        onClick: () => window.open(`https://wa.me/${r.phoneNumber}`, "_blank"),
      },
      {
        label: "Telegram",
        icon: <BrandTelegram size={16} />,
        visible: !!r.phoneNumber,
        onClick: () => window.open(`https://t.me/${r.phoneNumber}`, "_blank"),
      },
      {
        label: "Email",
        icon: <Envelope size={16} />,
        visible: !!r.email,
        onClick: () => {
          window.location.href = `mailto:${r.email}`;
        },
      },
      {
        label: `Switch to ${label === "players" ? "Representer" : "Player"}`,
        icon: label === "players" ? <UserCheck size={16} /> : <Users size={16} />,
        visible: !!onSwitchRole,
        onClick: () => onSwitchRole?.(row),
      },
      {
        label: "Delete",
        icon: <Trash size={16} />,
        tone: "danger",
        visible: !!(canDelete && onDelete && label === "tournaments" && !(row as ITournamentItem).isPublished),
        onClick: () => onDelete?.(row),
      },
      r.isBanned
        ? {
            label: <span className="data-table-menu-ok">Unban</span>,
            icon: <ShieldCheck size={16} className="data-table-menu-ok" />,
            visible: !!(canBan && onBan && label === "players"),
            onClick: () => onBan?.(row),
          }
        : {
            label: "Ban",
            icon: <Ban size={16} />,
            tone: "danger",
            visible: !!(canBan && onBan && label === "players"),
            onClick: () => onBan?.(row),
          },
      {
        label: "Delete",
        icon: <Trash size={16} />,
        tone: "danger",
        visible: !!(canDelete && onDelete && label !== "tournaments"),
        onClick: () => onDelete?.(row),
      },
    ];
  };

  const emptyState = (() => {
    const sp = new URLSearchParams(location.search);
    const hasSearchText = !!search || !!sp.get("search");
    const hasFilters = Array.from(sp.keys()).some(
      (k) => !["tab", "page", "sortColumn", "sortDirection", "listType"].includes(k) && k !== "search"
    );
    const isFiltered = hasSearchText || hasFilters;

    return (
      <EmptyState
        className="data-table-empty"
        icon={
          !isFiltered ? (
            label === "tournaments" ? (
              <Trophy />
            ) : label === "seasons" ? (
              <CalendarDays />
            ) : (
              <Users />
            )
          ) : (
            <FileSearch />
          )
        }
        title={isFiltered ? "No results" : label ? `No ${label}` : "No data"}
        description={
          isFiltered
            ? "No results match your search or filters"
            : label === "tournaments"
            ? "There are no tournaments created yet"
            : label === "seasons"
            ? "There are no seasons created yet"
            : label
            ? `There are no ${label} yet`
            : "No entries found"
        }
      />
    );
  })();

  // Under the list: the range shown and the page switcher
  const footer = (
    <div className="data-table-footer">
      <p className="data-table-range">
        Showing {data.length === 0 ? 0 : (pageNumber - 1) * pageSize + 1} -{" "}
        {data.length === 0 ? 0 : pageNumber * pageSize > totalItems ? totalItems : pageNumber * pageSize} of{" "}
        {data.length === 0 ? 0 : totalItems} {label}
      </p>
      <Pagination page={pageNumber} totalPages={totalPages} onChange={onPageChange} />
    </div>
  );

  return (
    <>
      {(hasSearch || includeGrid) && (
        <Toolbar
          className="data-table-toolbar"
          actions={
            includeGrid ? (
              <Segmented
                ariaLabel="View"
                value={selectedListOption}
                onChange={(v) => handleOptionClick(v)}
                options={[
                  { value: "grid", label: "Grid", icon: <Grid size={14} /> },
                  { value: "list", label: "List", icon: <List size={14} /> },
                ]}
              />
            ) : undefined
          }
        >
          {hasSearch && (
            <SearchInput
              className="data-table-search"
              inputRef={searchInputRef}
              value={searchValue !== undefined ? searchValue : search}
              onChange={(value) => {
                setSearch(value);
                if (onSearchChange) {
                  onSearchChange(value);
                } else if (onSearch) {
                  onSearch(value);
                }
              }}
              placeholder="Search by name, email..."
            />
          )}
        </Toolbar>
      )}
      {selectedListOption === "list" ? (
        <div className="data-table-container" id={id}>
          <TableWrap className="data-table" footer={footer}>
            {data.length > 0 || loading ? (
              <table className="ui-table ui-table--stack">
                <thead>
                  <tr>
                    {columns.map((col, index) => {
                      const thClass = [index === 0 ? "sticky-column" : "", col.stickyRight ? "sticky-column-right" : ""]
                        .filter(Boolean)
                        .join(" ");
                      return col.sortable ? (
                        <SortHeader
                          key={String(col.key)}
                          label={col.label}
                          active={sortColumn === col.key}
                          direction={sortDirection}
                          onClick={() => handleSort(col.key)}
                          className={thClass}
                        />
                      ) : (
                        <th key={String(col.key)} className={thClass}>
                          {col.label}
                        </th>
                      );
                    })}
                    {editable && <th className="sticky-header" aria-label="Actions" />}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr className="loading-row">
                      <td colSpan={columnCount} className="loading-cell">
                        <SkeletonRows rows={8} columns={Math.min(Math.max(columns.length, 2), 6)} />
                      </td>
                    </tr>
                  ) : (
                    data.map((row, rowIndex) => (
                      <tr key={rowIndex} className="is-clickable" onClick={() => onViewItem(row)}>
                        {columns.map((col, colIndex) => (
                          <td
                            key={String(col.key)}
                            // Phones stack each row into a card: the first column is its title, the others
                            // "LABEL … value" lines, the pinned (stickyRight) columns an action row at the bottom
                            data-label={colIndex === 0 || col.stickyRight || !col.label ? undefined : col.label}
                            className={`${colIndex === 0 ? "sticky-column ui-table__stack-title" : ""} ${col.stickyRight ? "sticky-column-right data-table-stack-actions" : ""} ${col.center ? "center" : ""} ${
                              col.uppercase ? "upper" : ""
                            }`}
                          >
                            {col.render ? col.render(row[col.key], row) : String(row[col.key])}
                          </td>
                        ))}
                        {editable && (
                          <td className="sticky-dots ui-table__actions" onClick={(e) => e.stopPropagation()}>
                            <Menu items={rowMenuItems(row)} />
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              emptyState
            )}
          </TableWrap>
        </div>
      ) : (
        <>
          {data.length > 0 ? (
            <div className="data-grid-container">
              {data.map((item, id) => {
                const tournamentItem = item as ITournamentItem;

                // Use status from API if available, otherwise fall back to computed status
                let status =
                  tournamentItem.status ||
                  (() => {
                    if (tournamentItem.isActive) return "Active";
                    if (tournamentItem.registrationOpen) return "Registration Open";
                    if (tournamentItem.registrationClosed) return "Registration Closed";
                    return "Registration Not Open";
                  })();

                // Capitalize "Completed" if it comes from API
                if (status.toLowerCase() === "completed") {
                  status = "Completed";
                }

                // For "upcoming" status, show registration status instead
                if (status.toLowerCase() === "upcoming") {
                  if (tournamentItem.registrationOpen) {
                    status = "Registration Open";
                  } else if (tournamentItem.registrationClosed) {
                    status = "Registration Closed";
                  } else {
                    status = "Registration Not Open";
                  }
                }

                const getStatusClass = () => {
                  const statusLower = status.toLowerCase();
                  // Check for "not open" first before "open" to avoid false matches
                  if (statusLower.includes("not open")) return "not-open";
                  if (statusLower.includes("completed")) return "completed";
                  if (statusLower.includes("active")) return "active";
                  if (statusLower.includes("open")) return "open";
                  if (statusLower.includes("closed")) return "closed";
                  return "not-open";
                };

                const statusClass = getStatusClass();

                return (
                  <div key={id} className="grid-item" onClick={() => onViewItem(item)}>
                    <div className="top-row">
                      <Avatar name={tournamentItem.name} src={tournamentItem.cover} size={44} className="cover-container" />
                      <div className="top-row-right">
                        <Tag tone={STATUS_TONES[statusClass]} className="status-tag" style={{ textTransform: "capitalize" }}>
                          {status}
                        </Tag>
                        {canDelete && onDelete && !tournamentItem.isPublished && (
                          <IconButton
                            label="Delete tournament"
                            icon={<Trash size={16} />}
                            variant="danger"
                            size="sm"
                            className="grid-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(item);
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div className="grid-data">
                      <p className="name">{tournamentItem.name}</p>
                      <div className="tournament-data-row">
                        <p>{tournamentItem.location?.name || "-"}</p>
                        <span className="separator" />
                        <p>Starts {moment(tournamentItem.startDate).format("DD MMM YYYY")}</p>
                      </div>
                      <div className="tournament-tags">
                        <Tag tone={sportTone(tournamentItem.type)}>{tournamentItem.type}</Tag>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="data-grid-placeholder">{loading ? <SkeletonRows rows={4} columns={3} /> : emptyState}</div>
          )}
          <div className="data-grid-footer">{footer}</div>
        </>
      )}
    </>
  );
};

export default DataTable;
